import { sanitizeIds } from "./sanitizeIds";

/** Marker embedded in every `NormalizedNetworkError` message. It is the classification contract
 * between this file and `isRawNetworkError` in `error.ts` — `name` cannot serve that role (see
 * the class doc below), so both sides must agree on this exact string. */
export const SAFARI_LOAD_FAILED_MARKER = "[Safari Load Failed]";

/**
 * Normalizes Safari's unhelpful "Load failed" TypeError with request context for monitoring.
 *
 * NOTE: `name` is deliberately left as `"Error"` — the Sentry issues for this class are titled
 * `Error: Network Error ... [Safari Load Failed]` and renaming it would re-title them mid-window.
 * That means connectivity classification cannot key off `name`: `isRawNetworkError` matches the
 * `[Safari Load Failed]` marker in the message instead. Keep that marker in the message.
 *
 * @see https://sentry.internal.classdojo-ops.com/organizations/classdojo/issues/?query=Load%20failed&sort=user&statsPeriod=7d
 */
export class NormalizedNetworkError extends Error {
  cause: unknown;
  tags: Record<string, string>;
  extra: Record<string, unknown>;
  fingerprint: string[];

  constructor(originalError: TypeError, method: string, path: string) {
    super(`Network Error ${method} ??? ${path} ${SAFARI_LOAD_FAILED_MARKER}`);
    this.cause = originalError;
    const sanitizedPath = sanitizeIds(path);
    this.tags = {
      errorType: "safari_load_failed",
      method,
    };
    this.extra = {
      path: sanitizedPath,
      originalErrorName: originalError.name,
      originalErrorMessage: originalError.message,
    };
    this.fingerprint = ["safari-load-failed"];
  }
}

/**
 * Converts Safari's generic "Load failed" TypeError to a structured error with request context.
 *
 * Safari throws `TypeError: Load failed` for network failures, CORS violations, content blocking,
 * and security restrictions - without specifics. Other browsers provide detailed messages.
 * The exact cause is unknown, so we add method/path context for Sentry grouping and debugging.
 *
 * @see https://sentry.internal.classdojo-ops.com/organizations/classdojo/issues/?query=Load%20failed&sort=user&statsPeriod=7d
 */
export function handleSafariSpecialCase(ex: unknown, { method, path }: { method: string; path: string }) {
  if (ex instanceof TypeError && ex.message.includes("Load failed")) {
    throw new NormalizedNetworkError(ex, method, path);
  }
}
